import React, { useState, useEffect, useMemo } from 'react';
import NoEvents from '../../../../../assets/icons/events/noevents.svg';
import { Container, Title } from '../body/workshops/WorkshopsStyled';
import EventCategoriesCard from '../card/EventCategories';
import { Icon, Select } from '@/presentation/components';
import EnterSearch from '@/presentation/reusables/EnterSearch';
import {
  addCustomDateFields,
  getSortedEventsNew,
  filterSubType,
  quitarTildes
} from '../body/EventUtils';
import Spinner from '@/presentation/components/Spinner';
import { IOption } from '@/presentation/components/Select/Interfaces';
import {
  Container as ContainInscription,
  Title as TitleInscription,
  Block
} from '../body/inscriptions/InscriptionsStyled';
import EventCards from '../body/inscriptions/EventCards';
import DefaultMessage from '../body/inscriptions/DefaultMessage';
import GoogleAnalytics from '@/presentation/hooks/useGoogleAnalytics';
import EventCardsWaitingList from '../body/inscriptions/EventCardsWaitingList';
import useEventSanctioned from '@/presentation/hooks/useEventSanctioned';
import ReusableInfo from '@/presentation/reusables/Info';
import { ButttonDS } from '@/presentation/reusables/Button/Button-DS';
import { Checkbox } from '@/presentation/components/Checkbox';
import Drawer from '@/presentation/components/Drawer';
import { SeparatorLine } from '@/presentation/components/Schedule/components/CourseDetail';
import ReusableButton from '@/presentation/reusables/Button';
import Link from '@/presentation/reusables/Link';

export function SearchResult() {
  return (
    <div
      className="defaultState"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingBottom: '82px',
        paddingTop: '16px'
      }}
    >
      <img
        style={{ marginBottom: '16px' }}
        src={NoEvents}
        width={96}
        height={96}
        alt="Sin eventos"
      />
      <p
        style={{
          paddingBottom: '24px',
          fontSize: '14px',
          lineHeight: '20px',
          fontWeight: '400',
          textAlign: 'center'
        }}
      >
        No se obtuvo ningún resultado para tu búsqueda
      </p>
      <div>
        <p
          style={{
            lineHeight: '20px',
            fontSize: '14px',
            fontWeight: '700'
          }}
        >
          Sugerencias:
        </p>
        <p>1. En el buscador:</p>
        <ul
          style={{
            listStyleType: 'disc',
            fontSize: '14px',
            lineHeight: '20px',
            fontWeight: '400',
            marginLeft: '25px'
          }}
        >
          <li>Comprueba que todas la palabras estén escritas correctamente</li>
          <li>Intenta usar otras palabras o términos más generales</li>
        </ul>
        <p>2. Modifica alguno de los filtros</p>
      </div>
    </div>
  );
}

const Alert = ({ message }: any) => (
  <ReusableInfo
    backgroundColor="#fabd00"
    iconColor="#161D1F"
    style={{ marginTop: '16px' }}
    text={message}
    textColor="#161D1F"
    internalPadding="16px"
  />
);

const SanctionedMessage = () => (
  <Alert message="<b>Tienes una sanción por no asistir 2 veces seguidas a un taller</b>. Mientras dure la sanción, no podrás inscribirte en talleres culturales o deportivos. Si tienes una justificación, <a class='link-warning' href='https://view.genially.com/67f45768e4aa82eeaf9e19fa' target='_blank'>sigue estos pasos.</a>" />
);

const MaximumRegisteredMessage = () => (
  <Alert message="¡Alcanzaste tu máximo de 2 talleres presenciales deportivos y/o culturales!" />
);

const RowRules: React.FC<any> = ({ children, icon }) => (
  <div style={{ display: 'flex', gap: '9px', padding: '24px 0' }}>
    <Icon name={icon} color="#3C4662" />
    {children}
  </div>
);

const NewRulesMessage = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(0);
  // detect resize and force re-render content icon
  useEffect(() => {
    const resize = () => setForceUpdate((n) => n + 1);
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  return (
    <>
      <div style={{ marginTop: 20 }}>
        Hay nuevas reglas para tus talleres.{' '}
        <Link
          linkType="void"
          onClick={(e) => {
            setIsOpen(true);
            e.preventDefault();
          }}
          linkName="Infórmate sobre los cambios."
        />
        {/* <button type="button" onClick={() => setIsOpen(true)}>
          Infórmate sobre los cambios.
        </button> */}
      </div>
      {isOpen && (
        <Drawer
          title="Nuevas reglas para tus talleres"
          closeHandler={() => setIsOpen(false)}
        >
          {/* key by re-rendering */}
          <div key={forceUpdate} style={{ marginTop: 8 }}>
            <RowRules icon="slash">
              <p>
                Si faltas dos veces seguidas a tu taller, no podrás inscribirte
                en este ni en el próximo periodo.
              </p>
            </RowRules>
            <SeparatorLine color="#A8CBE2" />
            <RowRules icon="users">
              <p>
                Solo puedes inscribirte a 2 talleres presenciales por ciclo. Si
                deseas participar en otro:
                <ul style={{ marginLeft: 20, listStyle: 'disc' }}>
                  <li>
                    Tendrás que anular tu inscripción en alguno de ellos y
                    elegir otro taller.
                  </li>
                  <li>Esperar al siguiente periodo.</li>
                </ul>
              </p>
            </RowRules>
            <SeparatorLine color="#A8CBE2" />
            <RowRules icon="map-pin">
              <p>
                Al inscribirte a tu segundo taller presencial deportivo o
                cultural, se te retirará automáticamente de las listas de
                espera.
              </p>
            </RowRules>
            <ReusableButton
              style={{ width: '100%' }}
              onClick={() => setIsOpen(false)}
            >
              Entendido
            </ReusableButton>
          </div>
        </Drawer>
      )}
    </>
  );
};

interface EventListViewProps {
  options: any[];
  handleTab: (selected: number) => void;
  selectEvent: (eventId: number, typeEvent: string) => void;
  tabTitle: string;
  current: any;
  eventType: string;
  loadMoreEvents: any;
  isLoading: boolean;
  showMore: boolean;
  data?: any;
}

const EventListView = (props: EventListViewProps) => {
  const {
    options,
    handleTab,
    selectEvent,
    tabTitle,
    current,
    eventType,
    loadMoreEvents,
    isLoading,
    showMore,
    data
  } = props;

  const isSanctioned = useEventSanctioned();

  const {
    eventsScrollType: {
      events = [],
      featured = [],
      eventsWaitingList = []
    } = {}
  } = data || {};

  const filterdEvents = useMemo(
    () => events?.filter((value: any) => value.isSubscribed === false),
    [events]
  );
  const mergedEvents = useMemo(
    () => [...(filterdEvents ?? []), ...(featured ?? [])],
    [filterdEvents, featured]
  );
  const sortedEvents = useMemo(
    () => getSortedEventsNew(addCustomDateFields(mergedEvents)),
    [mergedEvents]
  );
  const sortedWaintgListEvents = getSortedEventsNew(
    addCustomDateFields([...eventsWaitingList])
  );

  const countRegisteredEvents = events?.filter(
    (value: any) =>
      value.isSubscribed &&
      value.modality === 'P' &&
      value.eventType.id === 1 &&
      [1, 2].includes(value.eventSubtype.id)
  ).length;
  const maximunRegistered = countRegisteredEvents >= 2;
  const [dataEvents, setDataEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [hasAction, setHasAction] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selected, setSelected] = useState('Todos');
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    setLoading(true);
    let filtered = filterSubType(sortedEvents, selected, isSanctioned, checked);

    if (searchText.trim() !== '') {
      filtered = filtered.filter(
        (m: any) =>
          quitarTildes(m?.name)
            .toLowerCase()
            .includes(quitarTildes(searchText).toLowerCase()) ||
          searchText
            .split(' ')
            .every((word) =>
              quitarTildes(m?.name)
                .toLowerCase()
                .includes(quitarTildes(word).toLowerCase())
            )
      );
    }
    setDataEvents(filtered);
    setTimeout(() => setLoading(false), 200);
  }, [selected, checked, searchText, sortedEvents]);

  const changeSearchText = (evento: React.KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === 'Enter') {
      GoogleAnalytics.writeLog(
        'portal-services-filterSearchEvents',
        'services'
      );
      setHasAction(true);
      setLoading(true);
      setTimeout(() => setLoading(false), 500);
    }
  };

  const changeCheckbox = () => {
    GoogleAnalytics.writeLog('portal-services-checkboxEvents', 'services');
    setChecked(!checked);
  };

  const onToggleSelect = (e: IOption) => {
    GoogleAnalytics.writeLog(
      'portal-services-filterDropdownEvents',
      'services'
    );
    setSelected(e.label);
    setHasAction(true);
  };

  useEffect(() => {
    handleTab(current);
  }, []);

  const screenEvents = () => {
    if (isLoading) {
      return <Spinner />;
    }
    return (
      <>
        {isSanctioned && <SanctionedMessage />}
        {maximunRegistered && !isSanctioned && <MaximumRegisteredMessage />}
        <NewRulesMessage />
        <ContainInscription>
          <Block>
            <TitleInscription>Mis eventos inscritos</TitleInscription>
            {events?.length ? (
              <div data-testid="InscriptionsView" style={{ width: '100%' }}>
                <EventCards
                  events={addCustomDateFields(events)}
                  selectEvent={selectEvent}
                  isSubscribed
                />
              </div>
            ) : (
              <DefaultMessage />
            )}
          </Block>
          {sortedWaintgListEvents.length > 0 && (
            <Block>
              <TitleInscription>Lista de espera</TitleInscription>
              <div data-testid="InscriptionsView" style={{ width: '100%' }}>
                <EventCardsWaitingList
                  events={sortedWaintgListEvents}
                  selectEvent={selectEvent}
                />
              </div>
            </Block>
          )}
        </ContainInscription>
        <EventCategoriesCard
          dataEvents={dataEvents}
          selectEvent={selectEvent}
          isLoading={isLoading}
          loading={loading}
          hasAction={hasAction}
          tabTitle={tabTitle}
          isSanctioned={isSanctioned}
          eventType={eventType}
        />
      </>
    );
  };

  return (
    <div data-testid="cmp-eventlist-view">
      {eventType !== 'inscritos' ? (
        <>
          <Title>Filtro</Title>
          <Container>
            <div>
              <Select
                options={options}
                placeholder="Todos"
                handleChangeSelect={onToggleSelect}
              />
            </div>
            <div style={{ border: '1px solid #7a959f', borderRadius: '4px' }}>
              <EnterSearch
                value={searchText}
                onChange={changeSearchText}
                isLoadingData={isLoading}
                setSearchText={setSearchText}
                tabTitle={tabTitle}
              />
            </div>
            <div>
              <Checkbox
                onChange={changeCheckbox}
                label="Solo eventos con vacantes disponibles"
              />
            </div>
          </Container>
          {current === 2 && // WORKSHOPS
            isSanctioned && // is sanctioned
            ['Culturales', 'Deportivos', 'Todos'].includes(selected) && ( // only for these types
              <SanctionedMessage />
            )}
          {[1, 2].includes(current) && <NewRulesMessage />}
          <EventCategoriesCard
            dataEvents={dataEvents}
            selectEvent={selectEvent}
            isLoading={isLoading}
            loading={loading}
            hasAction={hasAction}
            tabTitle={tabTitle}
            isSanctioned={isSanctioned}
            eventType={eventType}
          />
        </>
      ) : (
        screenEvents()
      )}
      <>
        {dataEvents &&
          dataEvents.length === 0 &&
          !isLoading &&
          !loading &&
          eventType !== 'inscritos' && <SearchResult />}
      </>
      {!isLoading && !loading && eventType !== 'inscritos' && (
        <div
          style={{
            margin: '24px 0px',
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          {showMore ? (
            <ButttonDS
              name={`Ver más ${eventType}`}
              onClick={(e: any) => loadMoreEvents(e)}
            />
          ) : (
            dataEvents.length > 0 && (
              <b
                style={{
                  fontWeight: '400',
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#161D1F'
                }}
              >
                No hay más {eventType} por cargar
              </b>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default EventListView;
